// Shared metric condition config — used by AlertsSettings and Conversations filters.
// Single source of truth for operators, fields, and value options per result type.

export const OPERATORS_BY_TYPE = {
  boolean: [
    { value: "equals", label: "=" },
    { value: "not_equals", label: "!=" },
    { value: "has_value", label: "has value" },
    { value: "has_no_value", label: "has no value" },
  ],
  rating: [
    { value: "equals", label: "=" },
    { value: "not_equals", label: "!=" },
    { value: "greater_than", label: ">" },
    { value: "less_than", label: "<" },
    { value: "greater_than_or_equal", label: ">=" },
    { value: "less_than_or_equal", label: "<=" },
    { value: "has_value", label: "has value" },
    { value: "has_no_value", label: "has no value" },
  ],
  numeric: [
    { value: "equals", label: "=" },
    { value: "not_equals", label: "!=" },
    { value: "greater_than", label: ">" },
    { value: "less_than", label: "<" },
    { value: "greater_than_or_equal", label: ">=" },
    { value: "less_than_or_equal", label: "<=" },
    { value: "has_value", label: "has value" },
    { value: "has_no_value", label: "has no value" },
  ],
  enum: [
    { value: "equals", label: "=" },
    { value: "not_equals", label: "!=" },
    { value: "in", label: "in" },
    { value: "has_value", label: "has value" },
    { value: "has_no_value", label: "has no value" },
  ],
  standard: [
    { value: "equals", label: "=" },
    { value: "not_equals", label: "!=" },
    { value: "greater_than", label: ">" },
    { value: "less_than", label: "<" },
    { value: "greater_than_or_equal", label: ">=" },
    { value: "less_than_or_equal", label: "<=" },
  ],
  custom_value: [
    { value: "contains", label: "contains" },
    { value: "not_contains", label: "does not contain" },
    { value: "equals", label: "equals" },
    { value: "not_equals", label: "not equals" },
    { value: "has_value", label: "has value" },
    { value: "has_no_value", label: "has no value" },
  ],
};

export const STANDARD_METRIC_FIELDS = {
  latency_ms: [
    { value: "avg_ms", label: "Avg latency (ms)" },
    { value: "p95_ms", label: "P95 latency (ms)" },
    { value: "min_ms", label: "Min latency (ms)" },
    { value: "max_ms", label: "Max latency (ms)" },
    { value: "count", label: "Turn count" },
  ],
  user_interruptions: [{ value: "count", label: "Count" }],
  ai_interruptions: [{ value: "count", label: "Count" }],
  silence_detection: [
    { value: "count", label: "Silence count" },
    { value: "max_duration_ms", label: "Longest silence (ms)" },
    { value: "total_silence_ms", label: "Total silence (ms)" },
  ],
  stop_time_after_interruption: [
    { value: "avg_ms", label: "Avg stop time (ms)" },
    { value: "count", label: "Count" },
  ],
  word_per_minute: [
    { value: "avg_wpm", label: "WPM" },
  ],
  voice_tone_clarity: [
    { value: "overall_score", label: "Overall score (1-5)" },
    { value: "clarity_score", label: "Clarity score (1-5)" },
    { value: "tone_score", label: "Tone score (1-5)" },
  ],
  voice_change_detection: [
    { value: "count", label: "Change count" },
    { value: "avg_similarity", label: "Avg similarity (0-1)" },
  ],
  transcription_accuracy: [
    { value: "wer", label: "Word error rate (0-1)" },
  ],
};

export const BOOLEAN_VALUES = [
  { value: "pass", label: "Pass" },
  { value: "fail", label: "Fail" },
  { value: "not_applicable", label: "Not Applicable" },
];

export const NO_VALUE_OPERATORS = ["has_value", "has_no_value"];

// Determine the effective result type for a metric.
//
// This drives which operators and value input the condition builder shows, so it
// MUST match what is actually stored at result["value"] in metric_results_data
// (the store the alert evaluator + Conversations filter query):
//   - standard audio metrics  → field-based numeric stats (avg_ms, count, …)
//   - system LLM / custom      → the metric's declared result_type
//       compliance_check etc.  → "numeric" (stored as a 0-100 / 1-5 Float)
//       resolution_status       → "enum"   (stored as a string)
//   - only when no type is declared do we fall back to free-text matching.
export const getMetricResultType = (metric) => {
  if (!metric) return "boolean";
  const isStandard = metric.standard_metric_key && STANDARD_METRIC_FIELDS[metric.standard_metric_key];
  if (isStandard) return "standard";
  if (metric.result_type) return metric.result_type;
  const isSystemLlm = !metric.is_custom && metric.analysis_type === "transcript";
  return isSystemLlm ? "custom_value" : "boolean";
};

// Get field options for a metric (audio metrics have sub-fields)
export const getFieldOptions = (metric) => {
  if (!metric?.standard_metric_key) return null;
  return STANDARD_METRIC_FIELDS[metric.standard_metric_key] || null;
};

// Get operators for a metric
export const getOperators = (metric) => {
  const type = getMetricResultType(metric);
  return OPERATORS_BY_TYPE[type] || OPERATORS_BY_TYPE.boolean;
};
